const IMAGE_MODE_ZOOM = 'zoom';

/**
 * @description HarmonyProductImages implementation
 * @param {ProductImages} ProductImages widget for extending
 * @returns {typeof HarmonyProductImages}
 */
export default function (ProductImages) {
    return class HarmonyProductImages extends ProductImages {
        getOriginalImageSize(element) {
            return new Promise((resolve) => {
                const resultSize = {
                    src: element.dataset.originalSrc,
                    h: this.prefs().predefinedHeight,
                    w: this.prefs().predefinedWidth
                };
                const img = new Image();

                img.addEventListener('load', () => {
                    resultSize.h = img.height;
                    resultSize.w = img.width;
                    resolve(resultSize);
                });
                img.addEventListener('error', () => resolve(resultSize));
                img.src = element.dataset.originalSrc;
            });
        }

        initAndShowZoom(PhotoSwipe, PhotoSwipeUI, originalImagesSize) {
            const pswpElement = document.querySelectorAll('.pswp')[0];
            const imgCarousel = this.getById('imagesCarousel', (/** @type {carousel} */ carousel) => carousel);
            const thumbnailsCarousel = this.getById('imagesThumbnails', (/** @type {carousel} */ thumbnails) => thumbnails);
            const prefs = this.prefs();
            const options = {
                index: imgCarousel.getCurrentPageIndex(),
                loop: prefs.zoomLoop,
                history: false,
                clickToCloseNonZoomable: prefs.zoomClickToCloseNonZoomable,
                closeElClasses: prefs.zoomCloseElClasses.split(','),
                closeOnScroll: prefs.closeOnScroll,
                showHideOpacity: prefs.showHideOpacity
            };
            const gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI, originalImagesSize, options);

            gallery.listen('close', () => this.onZoomClosed());
            gallery.listen('beforeChange', () => {
                const currentPage = gallery.getCurrentIndex();

                imgCarousel.scrollToPage(currentPage);

                if (thumbnailsCarousel) {
                    thumbnailsCarousel.markCurrentPage(currentPage).scrollIntoView();
                }
            });
            gallery.init();

            this.gallery = gallery;
            this.imageMode = IMAGE_MODE_ZOOM;

            if (thumbnailsCarousel) {
                thumbnailsCarousel.toggleZoomState(true);
            }

            this.addGlobalDialogClass();
            this.addFocusTrap();

            // Update thumbnails carousel metrics/pagination
            thumbnailsCarousel.update();
        }

        loadPhotoswipeDependencies(carouselImages) {
            return Promise.all([
                import(/* webpackChunkName: 'photoswipe' */'photoswipe'),
                import(/* webpackChunkName: 'photoswipe' */'photoswipe/dist/photoswipe-ui-default.js'),
                Promise.all(Array.from(carouselImages).map(element => this.getOriginalImageSize(element)))
            ]);
        }

        zoom() {
            const imgCarousel = this.getById('imagesCarousel', carousel => carousel);
            const carouselImages = imgCarousel && imgCarousel.getImages();

            if (!carouselImages) {
                return;
            }

            this.loadPhotoswipeDependencies(carouselImages).then(([PhotoSwipe, PhotoSwipeUI, originalImagesSize]) => {
                this.initAndShowZoom(PhotoSwipe.default, PhotoSwipeUI.default, originalImagesSize);
            });

            this.eventBus().emit('gtm.pdp.zoom.click', {
                imagePosition: imgCarousel.currentActiveSlideIndex,
                imageName: imgCarousel.slides[imgCarousel.currentActiveSlideIndex].querySelector('img').alt || ''
            });
        }

        closeZoom() {
            super.closeZoom();

            this.getById('imagesThumbnails', thumbnails => {
                // Update thumbnails carousel metrics/pagination
                thumbnails.update();
            });
        }

        renderImages(product) {
            if (!product || !product.images || !product.images.large) {
                return;
            }

            product.images.large.forEach((element, index) => {
                const zoomImage = product.zoomImages.zoom && product.zoomImages.zoom[element.index];
                element.zoomUrl = zoomImage && zoomImage.url;
                element.isFirst = index === 0;
            });

            const pagination = product.images.large.length > 1
                ? new Array(product.images.large.length).fill(0).map((_el, i) => ({ page: i, pageIndex: i + 1 }))
                : [];

            this.render('galleryTemplate', {
                images: product.images,
                legendBadgeHtml: product.legendBadgeHtml,
                ratingBadgeShortHtml: product.ratingBadgeShortHtml,
                pagination
            }, this.ref('carouselInner')).then(() => {
                this.update();
            });
        }
    };
}
